/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Friday, November 12th 2021, 1:13:27 pm
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import { schemaBuilder } from "lib/validation";
import {
  ProcessTemplateArtifact,
  ProcessTemplateArtifactValidationSchema,
  ProcessTemplateArtifactSchemaType,
  ProcessDefinition,
} from "lib/schemas";
import { WritableModel } from "lib/schemas/abstract";
import { Many2ManyIntPKBaseSchema, Many2OneIntPKBaseSchema } from "lib/validation/base-validation-schemas";
import { PrimaryKeyValue } from "lib/types";

export enum FORM_STATUS_CHOICES_ENUM {
  Draft = 1,
  Published,
}

// Because of how TypeScript numeric enums work, below we only get the choices where the key is a number.
// In typescript the numeric enum {Draft, Published} resolves
// to an object with shape {"1": "Draft", "2": "Published", "Draft": 1, "Published": 2}
export const FORM_STATUS_CHOICES = {
  [FORM_STATUS_CHOICES_ENUM.Draft]: "Draft",
  [FORM_STATUS_CHOICES_ENUM.Published]: "Published",
};

export enum FORM_TYPES {
  application_form = "Process Submission Form",
  review_form = "Review Form",
  task_assignment_form = "Task Assignment Form",
  registration_form = "Registration Form",
  general_form = "General Form",
  payment_form = "Payment Form",
  survey_form = "Survey Form",
}

export interface IRegistrationFormSettings {
  profile_group: string;
  profile_group_description?: string;
  authorize: boolean;
  identification_field: string; // field_slug
  registration_email: string; // field_slug
  full_name: string; // field_slug
  first_name: string; // field_slug
  last_name: string; // field_slug
}

export interface FormConfiguration extends ProcessTemplateArtifact, WritableModel<false> {
  title: string;
  readonly slug?: string;
  intro?: string;
  button_text?: string;
  response?: string;
  redirect_url?: string;
  status: keyof typeof FORM_STATUS_CHOICES;
  publish_date?: Date;
  expiry_date?: Date;
  login_required: boolean;
  readonly identifier?: string;
  help_text?: string;
  form_type: keyof typeof FORM_TYPES;
  resubmit_button_text?: string;
  custom_resubmission_message?: string;
  archive_changed_data?: boolean;
  sequence?: number; //Foreign Key
  disable_new_submissions?: boolean;
  disable_new_submissions_message?: string;
  registration_form_settings?: IRegistrationFormSettings;
  sites: number[];
  readonly fields_count?: number;
  entry_number_default_prefix?: string;
  enforce_editable_fields_on_resubmission?: boolean;
  readonly page_count?: number;
}

export type FormAPIFilters = {
  form_type?: keyof typeof FORM_TYPES;
  process_definition?: PrimaryKeyValue<ProcessDefinition>;
};

export const RegistrationFormSettingsValidationSchema: schemaBuilder.SchemaOf<IRegistrationFormSettings> = schemaBuilder
  .object()
  .shape({
    profile_group: schemaBuilder.string().required("Registration Profile Group Title is required"),
    profile_group_description: schemaBuilder.string().nullable().optional(),
    authorize: schemaBuilder.boolean().required().default(true),
    identification_field: schemaBuilder
      .string()
      .nullable()
      .optional()
      .when("authorize", {
        is: true,
        then: schemaBuilder
          .string()
          .required(
            "You must select the form field that will identifier the user to register e.g. ID Number, Passport Number etc."
          ),
      }),
    registration_email: schemaBuilder
      .string()
      .nullable()
      .optional()
      .when("authorize", {
        is: true,
        then: schemaBuilder
          .string()
          .required("You must select the form field that will be used to register the user's email address."),
      }),
    full_name: schemaBuilder.string().optional(),
    first_name: schemaBuilder.string().when("full_name", {
      is: "",
      then: schemaBuilder.string().required("Kindly select the field for the user's first name."),
    }),
    last_name: schemaBuilder.string().when("full_name", {
      is: "",
      then: schemaBuilder.string().required("Kindly select the field for the user's last name."),
    }),
  });

type WriteOnlyFormConfiguration = Omit<FormConfiguration, "id" | "slug" | "identifier" | "fields_count" | "page_count">;

export const FormConfigurationValidationSchema: ProcessTemplateArtifactSchemaType<WriteOnlyFormConfiguration> =
  ProcessTemplateArtifactValidationSchema.shape({
    title: schemaBuilder.string().required("You must provide the title for a configured form."),
    intro: schemaBuilder.string().nullable().optional(),
    button_text: schemaBuilder.string().required("Submit button text is required."),
    response: schemaBuilder.string().nullable().optional(),
    redirect_url: schemaBuilder.string().nullable().optional(),
    status: schemaBuilder
      .mixed()
      .required("Status is required")
      .default(FORM_STATUS_CHOICES_ENUM.Draft)
      .test("status-choice-check", "Value must either 1 or 2", (value) =>
        Object.keys(FORM_STATUS_CHOICES)
          .map((c) => `${c}`)
          .includes(`${value}`)
      ), // Ensuring string values of the choices are allowed by using template string literals
    publish_date: schemaBuilder.date().nullable().optional(),
    expiry_date: schemaBuilder.date().nullable().optional(),
    login_required: schemaBuilder.boolean(),
    help_text: schemaBuilder.string().nullable().optional(),
    form_type: schemaBuilder.mixed().oneOf(Object.keys(FORM_TYPES)).required("Form Type is required"),
    resubmit_button_text: schemaBuilder.string().nullable().optional(),
    custom_resubmission_message: schemaBuilder.string().nullable().optional(),
    archive_changed_data: schemaBuilder.boolean(),
    // sequence: Many2OneIntPKBaseSchema().required("Application Number Sequence Configuration is required"),
    sequence: Many2OneIntPKBaseSchema().optional().nullable(),
    disable_new_submissions: schemaBuilder.boolean(),
    disable_new_submissions_message: schemaBuilder.string().nullable().optional(),
    registration_form_settings: RegistrationFormSettingsValidationSchema.optional().nullable().default(null),
    sites: Many2ManyIntPKBaseSchema().nullable().optional(),
    entry_number_default_prefix: schemaBuilder.string().optional().nullable(),
    enforce_editable_fields_on_resubmission: schemaBuilder.boolean().optional().nullable(),
  });
